import { createContext, useContext, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { formatMoney } from '@makila/fiscal';
import { mzDateTime } from '../lib/receipt';
import { post } from './api';
import { Alert, Button, Card } from './ui';
import { attempt, useLoad } from './useLoad';

export interface MakilaStatus {
  configured: boolean; linked: boolean; linkedAt: string | null; lastPullAt: string | null; lastPushAt: string | null; lastError: string | null;
  lastStatus: { company?: { legalName: string; nuit: string; trainingMode: boolean } | null } | null;
  pending: number; sent: number;
  rejected: { id: string; full_number: string; issue_date: string; gross_total: string; code: string; reason: string; attempts: number; next_attempt_at: string | null }[];
  log: { created_at: string; direction: 'in' | 'out'; kind: string; items: number; accepted: number; duplicates: number; rejected: number; error: string | null }[];
}

/** Empresa vinculada ao Makila: artigos, categorias e clientes passam a ser de lá. */
export const MakilaLinked = createContext(false);
export const useMakilaLinked = () => useContext(MakilaLinked);

/** Aviso nas páginas do catálogo quando o Makila é a fonte. */
export function ManagedByMakila({ what }: { what: string }) {
  return <Alert tone="info">Os {what} desta empresa são geridos no <b>Makila</b>: crie-os e altere-os lá e chegam aqui (e às caixas) sozinhos, cerca de um minuto depois.</Alert>;
}

const KIND: Record<string, string> = { catalog: 'Artigos e clientes do Makila', documents: 'Documentos enviados ao Makila', sync: 'Sincronização', 'catalog.products': 'Artigo do Makila', 'catalog.customers': 'Cliente do Makila' };

/** Estado da integração: o que já foi, o que espera, o que o Makila recusou e porquê. */
export function MakilaPage() {
  const { data, error, reload } = useLoad<MakilaStatus>('/makila/status');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <p className="text-muted">A carregar…</p>;
  const run = async (path: string) => { setBusy(true); setMsg(await attempt(() => post(path))); setBusy(false); void reload(); };
  const dt = (x: string | null) => (x ? mzDateTime(x) : '—');
  const co = data.lastStatus?.company;
  return (
    <div className="max-w-4xl space-y-4">
      {msg && <Alert>{msg}</Alert>}
      {data.lastError && <Alert>Última sincronização: {data.lastError}</Alert>}
      <Card title="Vínculo" actions={<Button onClick={() => run('/makila/sync')} disabled={busy}><RefreshCw size={16} className={busy ? 'animate-spin' : ''} /> Sincronizar agora</Button>}>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <div><dt className="text-sm text-muted">Empresa no Makila</dt><dd className="font-medium">{co ? `${co.legalName} · NUIT ${co.nuit}` : '—'}</dd></div>
          <div><dt className="text-sm text-muted">Vinculada desde</dt><dd>{dt(data.linkedAt)}</dd></div>
          <div><dt className="text-sm text-muted">Artigos e clientes recebidos</dt><dd>{dt(data.lastPullAt)}</dd></div>
          <div><dt className="text-sm text-muted">Documentos enviados</dt><dd>{dt(data.lastPushAt)}</dd></div>
        </dl>
        <p className="mt-3 text-sm text-muted">Com o vínculo, é o Makila que comunica estes documentos à AT. Enviados: <b>{data.sent}</b> · à espera: <b>{data.pending}</b> · recusados: <b>{data.rejected.length}</b>.</p>
      </Card>
      {data.rejected.length > 0 && (
        <Card title="Recusados pelo Makila" actions={<Button variant="ghost" onClick={() => run('/makila/retry')} disabled={busy}>Voltar a tentar já</Button>}>
          <p className="mb-3 text-sm text-muted">Cada um volta a ser tentado sozinho, com intervalos crescentes. Os seguintes da mesma série esperam por ele.</p>
          <ul className="divide-y divide-[var(--border)]">
            {data.rejected.map((r) => (
              <li key={r.id} className="py-2">
                <div className="flex flex-wrap justify-between gap-2"><span className="font-medium">{r.full_number}</span><span className="num">{formatMoney(r.gross_total)} MT</span></div>
                <div className="text-sm">{r.reason}</div>
                <div className="text-xs text-muted">{r.attempts} tentativa(s) · próxima {dt(r.next_attempt_at)}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card title="Registo">
        <ul className="divide-y divide-[var(--border)] text-sm">
          {data.log.map((l, i) => (
            <li key={i} className="flex flex-wrap gap-x-3 py-1.5">
              <span className="text-muted">{dt(l.created_at)}</span>
              <span className="flex-1">{KIND[l.kind] ?? l.kind}{l.error ? ` — ${l.error}` : ''}</span>
              <span className="num text-muted">{l.items} · {l.accepted} ok{l.duplicates ? ` · ${l.duplicates} repetidos` : ''}{l.rejected ? ` · ${l.rejected} recusados` : ''}</span>
            </li>
          ))}
          {!data.log.length && <li className="py-2 text-muted">Ainda sem sincronizações.</li>}
        </ul>
      </Card>
    </div>
  );
}
